import {
  Timestamp,
  collection,
  doc,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Form, StepperForm } from '@superfunnel/types/form';
import {
  HtmlStepperApiCall,
  HtmlStepperFormData,
} from '@superfunnel/types/html-stepper-form';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

export const formsColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'forms').withConverter(
    emptyConverter<Form>(),
  );
};

export const formsQuery = (accountId: string) => {
  return query(
    formsColRef(accountId),
    where('status', 'in', ['active', 'draft']),
  );
};

export const createForm = async (accountId: string, form: Form) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);
  const newFormRef = doc(collection(accountRef, 'forms'));

  return setDoc(
    newFormRef,
    { ...form, createdAt: Timestamp.now() },
    { merge: true },
  );
};

export const updateForm = async (
  accountId: string,
  formId: string,
  form: Form,
) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);
  const formRef = doc(collection(accountRef, 'forms'), formId);

  const formData: any = { ...form, updatedAt: Timestamp.now() };
  delete formData.createdBy;
  delete formData.createdAt;

  return setDoc(formRef, formData, { merge: true });
};

export const deleteForm = async (accountId: string, formId: string) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);
  const formRef = doc(collection(accountRef, 'forms'), formId);

  return updateDoc(formRef, { status: 'deleted' });
};

export const duplicateForm = async (
  accountId: string,
  formData: Form,
  userId: string,
) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);
  const formRef = doc(
    collection(accountRef, 'forms').withConverter(emptyConverter<Form>()),
  );

  return setDoc(formRef, {
    ...formData,
    createdAt: Timestamp.now(),
    createdBy: userId,
  });
};

export const generateStepperForm = async (
  accountId: string,
  prompt: string,
  baseLineForm: Partial<StepperForm> | null = null,
) => {
  const fn = httpsCallable(functions, 'accounts/forms/generateStepperForm');

  const res = await fn({
    accountId,
    prompt,
    save: false,
    form: baseLineForm,
  });

  return res.data;
};

export const testServerApiCall = async (
  accountId: string,
  apiCall: HtmlStepperApiCall,
  values: Record<string, string>,
): Promise<{ ok: boolean; status: number; body: string }> => {
  const fn = httpsCallable<
    {
      accountId: string;
      apiCall: HtmlStepperApiCall;
      values: Record<string, string>;
      currentStep: number;
      totalSteps: number;
    },
    { ok: boolean; status: number; body: string }
  >(functions, 'accounts/forms/testApiCall');

  const response = await fn({
    accountId,
    apiCall,
    values,
    currentStep: 1,
    totalSteps: 1,
  });
  return response.data;
};

// Fetch resolved production data for an HTML Stepper form (from Cloud Storage or legacy)
export const getFormProductionData = async (
  accountId: string,
  quizId: string,
  formId: string,
): Promise<HtmlStepperFormData> => {
  const fn = httpsCallable<
    { accountId: string; quizId: string; formId: string },
    HtmlStepperFormData
  >(functions, 'accounts/forms/getFormProductionData');

  const res = await fn({ accountId, quizId, formId });
  return res.data;
};
